// Story 10.1/10.2 — Cliente HTTP para el frontend. Usa cookies (Story 5.2
// auth) con `credentials: 'include'`. NO usa API Key (sería leak en el
// bundle JS). Story 10.2 suma helpers tipados (`botsApi.{list,get,remove,stats}`).

import type { AnalyticsReport } from './analytics-types';
import { toQueryString } from './conversation-filters';
import type { ConversationDetail, ConversationListResponse } from './conversations-types';
import type { FileContent, FileTreeNode, SaveFileResult } from './editor-types';
import type { ConversationFilters } from './query-keys';
import type { StatsSince } from './stats-types';

const DEFAULT_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiClient {
  get<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  delete<T>(path: string, init?: RequestInit): Promise<T>;
  /** Story 10.3 — endpoints paginados que devuelven el envelope completo. */
  getEnvelope<T>(path: string, init?: RequestInit): Promise<T>;
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string };
}

interface DataEnvelope<T> {
  data?: T;
}

/**
 * Story 10.5 — listeners globales para 401 mid-session. El gate del router
 * los lee y redirige a `/login` con toast "Tu sesión expiró".
 *
 * Las rutas de auth (`/auth/login`, `/auth/logout`) están exentas: un 401
 * en login es UX normal ("credenciales inválidas") y no debe disparar el
 * redirect global.
 */
type UnauthorizedListener = (path: string) => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

const AUTH_PATH_PREFIX = '/api/v1/auth/';

async function parseResponse<T>(res: Response, unwrap = true, requestPath = ''): Promise<T> {
  // 204 No Content → undefined.
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const parsed = text.length > 0 ? (JSON.parse(text) as DataEnvelope<T> | ErrorEnvelope) : null;
  if (!res.ok) {
    const err = (parsed as ErrorEnvelope)?.error;
    if (res.status === 401 && !requestPath.startsWith(AUTH_PATH_PREFIX)) {
      // Notifica los listeners (router redirect + toast). No await — los
      // listeners son sync y el caller maneja su propio error abajo.
      for (const listener of unauthorizedListeners) listener(requestPath);
    }
    throw new ApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? res.statusText,
      parsed,
    );
  }
  if (unwrap && parsed && typeof parsed === 'object' && 'data' in parsed) {
    return (parsed as DataEnvelope<T>).data as T;
  }
  return parsed as T;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? DEFAULT_BASE;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
    unwrap = true,
  ): Promise<T> {
    const url = `${baseUrl}${path}`;
    const headers: HeadersInit = {
      Accept: 'application/json',
      ...(init?.headers ?? {}),
    };
    const reqInit: RequestInit = {
      method,
      credentials: 'include',
      ...init,
      headers,
    };
    if (body !== undefined) {
      reqInit.body = JSON.stringify(body);
      (reqInit.headers as Record<string, string>)['Content-Type'] = 'application/json';
    }
    const res = await fetchImpl(url, reqInit);
    return parseResponse<T>(res, unwrap, path);
  }

  return {
    get: (path, init) => request('GET', path, undefined, init),
    post: (path, body, init) => request('POST', path, body, init),
    put: (path, body, init) => request('PUT', path, body, init),
    delete: (path, init) => request('DELETE', path, undefined, init),
    getEnvelope: (path, init) => request('GET', path, undefined, init, false),
  };
}

export const api = createApiClient();

// Story 10.2 — typed helpers consumidos por el dashboard.

export interface BotRowSummary {
  id: number;
  name: string;
  status: 'inactive' | 'active' | 'error';
  phoneNumberId: string | null;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface BotStatsResponse {
  conversations: number;
  messagesReceived: number;
  messagesSent: number;
  flowsCompleted: number;
  flowErrors: number;
  partial: boolean;
}

// Story 13.1 — Templates discoverable + creación desde wizard.
export interface TemplateMetadataDto {
  name: string;
  description: string;
  placeholders: string[];
  requiredAnswers: string[];
  category?: 'starter' | 'business' | 'sample';
  version?: string;
  businessTypes?: string[];
  enums?: Record<string, string[]>;
  /** Story 17-5 — valor de un campo opcional que el operador deja vacío. */
  defaults?: Record<string, string>;
  /** Story 13.x — origen del template (builtin del repo o custom del user). */
  kind?: 'builtin' | 'user';
}

export interface TemplateBundleDto {
  metadata: TemplateMetadataDto;
  files: Record<string, string>;
}

export interface CreateBotFromTemplateBody {
  templateName: string;
  /** Story 16.2 — obligatorio si el usuario puede crear en varias organizaciones. */
  organizationId?: number;
  answers: Record<string, string>;
  voice?: {
    tone?: 'friendly' | 'formal' | 'casual';
    pronouns?: 'tu' | 'usted';
    emojiLevel?: 'none' | 'minimal' | 'moderate' | 'heavy';
  };
  provider: 'cloud-api' | 'evolution-go';
  /** Story 13.x cleanup — datos operacionales NO secretos del provider. */
  providerConfig?: {
    baseUrl?: string;
    instanceName?: string;
    phoneNumberId?: string;
  };
  /** Realismo de tiempo. Opcional — el endpoint usa defaults razonables si falta. */
  timing?: {
    defaultMessageDelayMs?: number;
    userBurstWindowMs?: number;
  };
}

export interface CreateBotFromTemplateResponse {
  bot: BotRowSummary;
  files: Record<string, string>;
}

export const botsApi = {
  list: () => api.get<BotRowSummary[]>('/api/v1/bots'),
  get: (id: number) => api.get<BotRowSummary>(`/api/v1/bots/${id}`),
  remove: (id: number) => api.delete<void>(`/api/v1/bots/${id}`),
  /** Story 13.1 — lista templates discoverable en el server (cache 60s). */
  listTemplates: () => api.get<TemplateMetadataDto[]>('/api/v1/bots/templates'),
  /** Story 13.1 — devuelve metadata + archivos del template para preview. */
  getTemplate: (name: string) =>
    api.get<TemplateBundleDto>(`/api/v1/bots/templates/${encodeURIComponent(name)}`),
  /** Story 13.1 — crea un bot desde template con substitución + inyección
   *  de voice/whatsapp.provider. */
  createFromTemplate: (body: CreateBotFromTemplateBody) =>
    api.post<CreateBotFromTemplateResponse>('/api/v1/bots/from-template', body),
  stats: (id: number, since: StatsSince) =>
    api.get<BotStatsResponse>(`/api/v1/bots/${id}/stats?since=${since}`),
  /** Story 13.x — guardar el bundle del bot actual como template del user. */
  saveTemplate: (body: {
    name: string;
    description: string;
    metadata?: Partial<TemplateMetadataDto>;
    files: Record<string, string>;
  }) =>
    api.post<{ id: number; name: string; metadata: Record<string, unknown> }>(
      '/api/v1/bots/templates',
      body,
    ),
  /** Story 13.x — borra un template custom del user. */
  deleteTemplate: (name: string) =>
    api.delete<void>(`/api/v1/bots/templates/${encodeURIComponent(name)}`),
};

// Story 10.4 — analytics.
export const analyticsApi = {
  get: (botId: number, params: { since?: string; until?: string }) => {
    const qs = new URLSearchParams();
    if (params.since) qs.set('since', params.since);
    if (params.until) qs.set('until', params.until);
    const tail = qs.toString();
    return api.get<AnalyticsReport>(`/api/v1/bots/${botId}/analytics${tail ? `?${tail}` : ''}`);
  },
};

// Story 10.3 — conversaciones.
export const conversationsApi = {
  list: (botId: number, filters: ConversationFilters & { limit?: number; offset?: number }) => {
    const qs = toQueryString(filters);
    const path = `/api/v1/bots/${botId}/conversations${qs ? `?${qs}` : ''}`;
    return api.getEnvelope<ConversationListResponse>(path);
  },
  get: (botId: number, sessionId: number) =>
    api.get<ConversationDetail>(`/api/v1/bots/${botId}/conversations/${sessionId}`),
};

// Story 11.1 — editor de archivos.
export const filesApi = {
  tree: (botId: number) => api.get<FileTreeNode[]>(`/api/v1/bots/${botId}/files`),
  content: (botId: number, path: string) =>
    api.get<FileContent>(`/api/v1/bots/${botId}/files/content?path=${encodeURIComponent(path)}`),
  // P-336 (review 11.1): removido `force?: boolean` del shape público. El
  // endpoint backend lo soporta para recovery admin-direct, pero la UI no
  // debe ofrecerlo nunca — bypassea el guard de validación.
  save: (botId: number, input: { path: string; content: string }) =>
    api.put<SaveFileResult>(`/api/v1/bots/${botId}/files`, input),
};

// Story 11.4 — Deploy/Rollback/Deployments. Reusa endpoints de Story 6.5.

export interface DeploymentRow {
  id: number;
  botId: number;
  version: string;
  status: 'active' | 'superseded' | 'rolled-back';
  notes: string | null;
  createdAt: string;
  deployedBy: number | null;
}

export interface DeployInput {
  version?: string;
  notes?: string | null;
  /** Config override opcional; si no se pasa, el backend usa el config actual del bot. */
  config?: Record<string, unknown>;
}

export interface RollbackInput {
  targetVersion?: string;
  notes?: string | null;
}

export interface DeploymentDetail extends DeploymentRow {
  /** Snapshot del config activo ANTES de este deploy (rollback target). */
  config: Record<string, unknown>;
  /** Config que quedó activo al hacer este deploy. */
  newConfig: Record<string, unknown>;
}

export const deploymentsApi = {
  list: (botId: number, opts: { limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit != null) qs.set('limit', String(opts.limit));
    if (opts.offset != null) qs.set('offset', String(opts.offset));
    const tail = qs.toString();
    return api.get<DeploymentRow[]>(`/api/v1/bots/${botId}/deployments${tail ? `?${tail}` : ''}`);
  },
  /** Story 11.x — devuelve el row completo con `config` + `newConfig`. */
  getDetail: (botId: number, deploymentId: number) =>
    api.get<DeploymentDetail>(`/api/v1/bots/${botId}/deployments/${deploymentId}`),
  deploy: (botId: number, body: DeployInput) =>
    api.post<{ bot: BotRowSummary; deployment: DeploymentRow }>(
      `/api/v1/bots/${botId}/deploy`,
      body,
    ),
  rollback: (botId: number, body: RollbackInput) =>
    api.post<{
      bot: BotRowSummary;
      fromDeployment: DeploymentRow | null;
      toDeployment: DeploymentRow;
    }>(`/api/v1/bots/${botId}/rollback`, body),
};
